/**
 * The two games the table itself plays, on the table projector, with the phones as the hands.
 *
 * - **Pass it on (2617).** Underground, people trade in the dark, hand to hand, barely speaking. A
 *   light sits at one guest's place; their phone says it is with them, and a tap passes it on to
 *   someone else at the table — nobody knows who until it lands (Marek, 2026-09-28: at random, but
 *   everyone once before anyone twice, and never straight back). When it has been with everyone,
 *   that is once round the table. Nobody can hurry it but the person holding it, so the table
 *   watches the light travel and tells the slow one to pass.
 * - **Plant (2791).** Back on the surface, they grow things. Everyone taps once and something grows
 *   at their own place; when the whole table has planted, the strip turns to a garden.
 *
 * Started by the captain from the console, never by the clock: they belong to a moment, not to a
 * minute. Neither can hurt the crew. Pure rules only; the reducer owns the state.
 */
import { isAboard } from './state.js';
import type { Seat } from './types.js';

export type TableGameKind = 'pass' | 'plant';

export interface TableGame {
  kind: TableGameKind;
  chapter: number;
  startedAt: number;
  /** It stops by itself here. */
  endsAt: number;
  /** Pass it on: whose place the light is at, and where it came from, for drawing it travelling. */
  holder: number | null;
  from: number | null;
  passedAt: number | null;
  /** How many hands it has been through, and how many times round the whole table. */
  passes: number;
  laps: number;
  /**
   * Pass it on: who has had the light this time round, the holder included. It goes to someone
   * not on this list; once everyone is, that was a lap, and the list starts again from the holder.
   * Absent on a game started before 2026-09-29.
   */
  had?: number[];
  /** Pass it on: where the random order starts, so every screen works out the same next guest. */
  seed?: number;
  /** Plant: seat → when they planted. */
  planted: Record<string, number>;
}

export const TABLE_GAME = {
  /** How long a table game runs before it ends itself. */
  PASS_MS: 120_000,
  PLANT_MS: 60_000,
  /** A pass sooner than this after the last one is a double tap, not a pass. */
  PASS_GAP_MS: 400,
  /**
   * With hands (the cockpit iPad's camera, `tableHand`): the light settles this long at a place
   * before a hand there can send it on — long enough that one hand cannot pass it twice, nor send it
   * straight back as it lands, short enough that a hand put down at the light passes it at once
   * (Marek, 2026-10-06: "it takes a lot of time and effort to activate"; 1.5 s before).
   */
  HAND_SETTLE_MS: 400,
  /**
   * Points (Marek, 2026-09-27: small next to a phone game's 1 000 at par): each pass, each planting,
   * and for everyone at the table when the light has gone all the way round or the whole table has
   * planted.
   */
  PASS_POINTS: 100,
  PLANT_POINTS: 250,
  TABLE_BONUS: 200,
} as const;

/** The era each game belongs to. The captain can still start either at any time. */
export const TABLE_GAME_ERA: Record<TableGameKind, number> = { pass: 4, plant: 5 };

/**
 * Who plays the table games: every guest aboard (`isAboard`: checked in, not voted out) — the same
 * guests the TV and the phones count. A guest the captain has just moved to another seat still
 * counts before their phone picks up its new pass: a hand over their place plays for them.
 * In order round the table: down the odd side from the screen to the far end, back up the even side.
 */
export function tablePlayers(seats: readonly Pick<Seat, 'seat' | 'callsign' | 'profession' | 'brig'>[]): number[] {
  const aboard = seats.filter(isAboard).map((s) => s.seat);
  const odd = aboard.filter((n) => n % 2 === 1).sort((a, b) => a - b);
  const even = aboard.filter((n) => n % 2 === 0).sort((a, b) => b - a);
  return [...odd, ...even];
}

/** The room's older name for `tablePlayers`. */
export const roundTheTable = tablePlayers;

/** A small, even spread of a number, the same on every machine. */
function mix(...parts: number[]): number {
  let h = 0x811c9dc5;
  for (const c of parts.join(':')) {
    h ^= c.charCodeAt(0);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * Who the light goes to next, or null when nobody is aboard to take it: someone who has not had it
 * this time round, never the one who just passed it here if there is anyone else, picked from the
 * game's seed and its pass count — so the room, the phones and the TV all name the same guest.
 * Worked out from the table as it is now: a guest who sits down mid-game is in the draw at once.
 */
export function nextHolder(
  game: Pick<TableGame, 'holder' | 'from' | 'passes' | 'startedAt' | 'had' | 'seed'>,
  order: readonly number[],
): number | null {
  if (order.length === 0) return null;
  if (game.holder === null) return order[0] ?? null;
  const others = order.filter((s) => s !== game.holder);
  // Alone at the table: it can only come back.
  if (others.length === 0) return game.holder;
  const had = game.had ?? [];
  let pool = others.filter((s) => !had.includes(s));
  if (pool.length === 0) pool = others;
  const notBack = pool.filter((s) => s !== game.from);
  if (notBack.length > 0) pool = notBack;
  return pool[mix(game.seed ?? game.startedAt, game.passes) % pool.length] ?? null;
}

/** A new table game. Pass it on starts with a deterministic guest, so every screen agrees. */
export function startTableGame(
  kind: TableGameKind,
  chapter: number,
  order: readonly number[],
  now: number,
): TableGame {
  const first = order.length ? (order[Math.floor(now / 1000) % order.length] ?? null) : null;
  return {
    kind,
    chapter,
    startedAt: now,
    endsAt: now + (kind === 'pass' ? TABLE_GAME.PASS_MS : TABLE_GAME.PLANT_MS),
    holder: kind === 'pass' ? first : null,
    from: null,
    passedAt: kind === 'pass' ? now : null,
    passes: 0,
    laps: 0,
    had: kind === 'pass' && first !== null ? [first] : [],
    seed: now,
    planted: {},
  };
}

/** The light moves on. Only its holder can pass it; a second tap straight after is ignored. */
export function passOn(game: TableGame, seat: number, order: readonly number[], now: number): TableGame | string {
  if (game.kind !== 'pass') return 'not this game';
  if (game.holder !== seat) return 'it is not with you';
  if (game.passedAt !== null && now - game.passedAt < TABLE_GAME.PASS_GAP_MS) return 'already passed';
  const next = nextHolder(game, order);
  if (next === null) return 'nobody to pass to';
  const had = [...new Set([...(game.had ?? [seat]), next])];
  // Everyone at the table has had it: once round. The next round starts with the one holding it.
  const lap = order.every((s) => had.includes(s));
  return {
    ...game,
    holder: next,
    from: seat,
    passedAt: now,
    passes: game.passes + 1,
    laps: game.laps + (lap ? 1 : 0),
    had: lap ? [next] : had,
  };
}

/** Something grows at a guest's place. Once each. */
export function plantAt(game: TableGame, seat: number, now: number): TableGame | string {
  if (game.kind !== 'plant') return 'not this game';
  if (game.planted[String(seat)] !== undefined) return 'already planted';
  return { ...game, planted: { ...game.planted, [String(seat)]: now } };
}

/** Whether the whole table has planted. */
export function gardenGrown(game: TableGame, order: readonly number[]): boolean {
  return game.kind === 'plant' && order.length > 0 && order.every((s) => game.planted[String(s)] !== undefined);
}

export function tableGameOver(game: TableGame, now: number): boolean {
  return now >= game.endsAt;
}

/** Two guests changed places: the light and the plants go with them. */
export function moveTableSeats(game: TableGame | null, move: (seat: number) => number): TableGame | null {
  if (!game) return game;
  return {
    ...game,
    holder: game.holder === null ? null : move(game.holder),
    from: game.from === null ? null : move(game.from),
    ...(game.had ? { had: game.had.map(move) } : {}),
    planted: Object.fromEntries(Object.entries(game.planted).map(([seat, at]) => [String(move(Number(seat))), at])),
  };
}

/** A guest left the table: the light moves on from them, and their plant goes. */
export function leaveTable(game: TableGame | null, seat: number, order: readonly number[], now: number): TableGame | null {
  if (!game) return game;
  const { [String(seat)]: _gone, ...planted } = game.planted;
  const rest = order.filter((s) => s !== seat);
  const had = (game.had ?? []).filter((s) => s !== seat);
  if (game.holder !== seat) return { ...game, planted, ...(game.had ? { had } : {}) };
  // Hand it on as if they had passed it, to someone still at the table.
  const holder = rest.length > 0 ? nextHolder({ ...game, had, holder: -1, from: null }, rest) : null;
  return { ...game, planted, holder, from: null, passedAt: now, had: holder === null ? [] : [...new Set([...had, holder])] };
}
